import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import antdTheme from './theme/antdTheme';
import AppLayout from './components/AppLayout';
import TeacherLayout from './components/TeacherLayout';
import Login from './pages/Login';

// 页面按路由懒加载，拆分成独立 chunk，首屏只下载登录页
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AssessmentCenter = lazy(() => import('./pages/AssessmentCenter'));
const GrowthProfile = lazy(() => import('./pages/GrowthProfile'));
const AdaptiveAssessment = lazy(() => import('./pages/AdaptiveAssessment'));
const TeacherDashboard = lazy(() => import('./pages/teacher/TeacherDashboard'));
const StudentList = lazy(() => import('./pages/teacher/StudentList'));
const StudentProfile = lazy(() => import('./pages/teacher/StudentProfile'));
const AddStudent = lazy(() => import('./pages/teacher/AddStudent'));
const RecordEvent = lazy(() => import('./pages/teacher/RecordEvent'));
const AssessmentTasks = lazy(() => import('./pages/teacher/AssessmentTasks'));
const EventDetail = lazy(() => import('./pages/teacher/EventDetail'));
const TeacherMe = lazy(() => import('./pages/teacher/TeacherMe'));
const GrowthReport = lazy(() => import('./pages/GrowthReport'));
const ClassAlbum = lazy(() => import('./pages/ClassAlbum'));
const Mailbox = lazy(() => import('./pages/Mailbox'));
const Leave = lazy(() => import('./pages/Leave'));
const TeacherAttendance = lazy(() => import('./pages/teacher/TeacherAttendance'));
const Chain = lazy(() => import('./pages/Chain'));
const TeacherChain = lazy(() => import('./pages/teacher/TeacherChain'));
const Courses = lazy(() => import('./pages/Courses'));
const TeacherCourses = lazy(() => import('./pages/teacher/TeacherCourses'));
const FamilyServices = lazy(() => import('./pages/FamilyServices'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ResetParentPassword = lazy(() => import('./pages/teacher/ResetParentPassword'));
// AI 成长大脑 + GE3000 家庭成长画像
const GrowthBrain = lazy(() => import('./pages/GrowthBrain'));
const FamilyEntry = lazy(() => import('./pages/family/FamilyEntry'));
const FamilyPrivacy = lazy(() => import('./pages/family/FamilyPrivacy'));
const FamilyStep = lazy(() => import('./pages/family/FamilyStep'));
const FamilyConfirm = lazy(() => import('./pages/family/FamilyConfirm'));
const FamilyGenerating = lazy(() => import('./pages/family/FamilyGenerating'));
const FamilyReport = lazy(() => import('./pages/family/FamilyReport'));
const TeacherFamily = lazy(() => import('./pages/teacher/TeacherFamily'));
const TeacherFamilyDetail = lazy(() => import('./pages/teacher/TeacherFamilyDetail'));
const TeacherGuide = lazy(() => import('./pages/teacher/TeacherGuide'));
const ResourceMap = lazy(() => import('./pages/teacher/ResourceMap'));
// 学年管理 / 班级管理 / 升年级中心（管理员，入口在「我的」页）
const AcademicYears = lazy(() => import('./pages/teacher/AcademicYears'));
const ClassManage = lazy(() => import('./pages/teacher/ClassManage'));
const PromotionCenter = lazy(() => import('./pages/teacher/PromotionCenter'));
const PromotionHistory = lazy(() => import('./pages/teacher/PromotionHistory'));
// 课表：一个查看页自适应三种角色，导入页只给 admin
const Schedule = lazy(() => import('./pages/Schedule'));
const ScheduleImport = lazy(() => import('./pages/teacher/ScheduleImport'));
// 社团 + 社团/晚自习点名。⚠️跟 TeacherAttendance（课堂考勤请假审核）是两套
// 独立功能，入口也分开放 —— 混在一个页面里会让老师分不清在给哪个场合点名
const ClubManage = lazy(() => import('./pages/teacher/ClubManage'));
const ClubDetail = lazy(() => import('./pages/teacher/ClubDetail'));
const TakeAttendance = lazy(() => import('./pages/teacher/TakeAttendance'));
const TeacherAdmin = lazy(() => import('./pages/teacher/TeacherAdmin'));

const PageLoading = () => <Spin size="large" style={{ display: 'block', margin: '120px auto' }} />;

function PrivateRoute({ children, roles }: { children: React.ReactElement; roles?: string[] }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // 强制改密守卫：还在用初始密码/临时密码的人，手动敲 URL 也进不了业务页。
  // 放在角色判断之前，否则会先被按角色弹到首页、绕开改密。
  if (user.mustChangePwd) {
    return <Navigate to="/change-password?forced=1" replace />;
  }

  if (roles) {
    if (!roles.includes(user.role)) {
      // 角色不匹配：按角色送回各自的首页
      const home = user.role === 'teacher' || user.role === 'admin' ? '/teacher' : '/home';
      return <Navigate to={home} replace />;
    }
  }
  return children;
}

// 改密页专用守卫：只要登录了就能进（不能套 PrivateRoute，否则强制改密的人
// 会被守卫无限重定向到这一页自己）。
function AuthedRoute({ children }: { children: React.ReactElement }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return children;
}

function RoleRedirect() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role === 'teacher' || user.role === 'admin') {
    return <Navigate to="/teacher" replace />;
  }
  return <Navigate to="/home" replace />;
}

function App() {
  return (
    <ConfigProvider locale={zhCN} theme={antdTheme}>
      <BrowserRouter>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* 改密页走 AuthedRoute：强制改密的人必须能进得来 */}
          <Route path="/change-password" element={<AuthedRoute><ChangePassword /></AuthedRoute>} />

          <Route path="/" element={<PrivateRoute><RoleRedirect /></PrivateRoute>} />

          {/* Student/Parent routes */}
          <Route path="/home" element={<PrivateRoute roles={['student', 'parent']}><AppLayout /></PrivateRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="services" element={<FamilyServices />} />
            <Route path="assessment" element={<AssessmentCenter />} />
            <Route path="assessment/start" element={<AdaptiveAssessment />} />
            <Route path="profile" element={<GrowthProfile />} />
            <Route path="album" element={<ClassAlbum />} />
            <Route path="mailbox" element={<Mailbox />} />
            <Route path="leave" element={<Leave />} />
            <Route path="chain" element={<Chain />} />
            <Route path="courses" element={<Courses />} />
            {/* 课表。跟 /teacher/schedule 是同一个组件，内部按角色自适应：
                家长/学生只看自己孩子那个班，没有班级切换器（后端对别的班 403） */}
            <Route path="schedule" element={<Schedule />} />
            {/* AI 成长大脑 */}
            <Route path="brain" element={<GrowthBrain />} />
            {/* GE3000 家庭成长画像 */}
            <Route path="family" element={<FamilyEntry />} />
            <Route path="family/privacy" element={<FamilyPrivacy />} />
            <Route path="family/step/:step" element={<FamilyStep />} />
            <Route path="family/confirm" element={<FamilyConfirm />} />
            <Route path="family/generating" element={<FamilyGenerating />} />
            <Route path="family/report" element={<FamilyReport />} />
          </Route>

          {/* Teacher routes - mobile first */}
          <Route path="/teacher" element={<PrivateRoute roles={['teacher', 'admin']}><TeacherLayout /></PrivateRoute>}>
            <Route index element={<TeacherDashboard />} />
            <Route path="students" element={<StudentList />} />
            <Route path="students/add" element={<AddStudent />} />
            <Route path="student/:studentId" element={<StudentProfile />} />
            <Route path="student/:studentId/report" element={<GrowthReport />} />
            <Route path="record" element={<RecordEvent />} />
            <Route path="event/:eventId" element={<EventDetail />} />
            <Route path="assessments" element={<AssessmentTasks />} />
            <Route path="album" element={<ClassAlbum />} />
            <Route path="mailbox" element={<Mailbox />} />
            <Route path="attendance" element={<TeacherAttendance />} />
            <Route path="chain" element={<TeacherChain />} />
            <Route path="courses" element={<TeacherCourses />} />
            <Route path="reset-password" element={<ResetParentPassword />} />
            {/* AI 成长大脑 + 家庭画像（教师端）*/}
            <Route path="brain" element={<GrowthBrain />} />
            <Route path="family" element={<TeacherFamily />} />
            <Route path="family/:studentId" element={<TeacherFamilyDetail />} />
            <Route path="guide" element={<TeacherGuide />} />
            <Route path="resource-map" element={<ResourceMap />} />
            <Route path="me" element={<TeacherMe />} />
            {/* 学年制管理。⚠️这四个页面只对 admin 有意义，但路由仍挂在
                teacher|admin 的 TeacherLayout 下 —— 页面内部的接口全是
                requireRole('admin')，老师误敲 URL 进来只会看到空列表和 403 提示。
                单独加一层角色守卫会让「班主任兼管理员」这种账号进不来 */}
            <Route path="academic-years" element={<AcademicYears />} />
            <Route path="classes" element={<ClassManage />} />
            <Route path="promotion" element={<PromotionCenter />} />
            <Route path="promotion-history" element={<PromotionHistory />} />
            {/* 课表。导入只对 admin 有意义（接口是 requireRole('admin')），
                查看所有角色都能进 —— 家长/学生走 /home/schedule 那条 */}
            <Route path="schedule" element={<Schedule />} />
            <Route path="schedule-import" element={<ScheduleImport />} />
            {/* 教师账号管理。后端 /api/teacher-admin 全部接口都是 requireRole('admin')，
                这里不额外做前端拦截 —— 入口只对 admin 显示，直接敲 URL 进来也只会
                看到空列表 + 401，不会泄露数据 */}
            <Route path="admin/teachers" element={<TeacherAdmin />} />
            {/* 社团 + 点名。⚠️这三页对**普通教师**开放，不是 admin 专属：
                已定案「教师自己就能建社团」，而点名权按社团 advisorIds 判
                （社团跳班，班级口径卡不住）。后端 routes/club.js 和
                attendanceSession.js 是 requireRole('teacher','admin') + 逐条
                查指导老师身份，前端不重复拦 */}
            <Route path="clubs" element={<ClubManage />} />
            <Route path="club/:id" element={<ClubDetail />} />
            <Route path="take-attendance" element={<TakeAttendance />} />
          </Route>

          {/* Shared report page (parent/student view) */}
          <Route path="/report/:studentId" element={<PrivateRoute><GrowthReport /></PrivateRoute>} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
